// src/pages/PersonalSettingsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  Divider,
  Stack,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { validateTCKN, validatePassport, isTurkishNationality } from '../shared/validators';

interface ProfileFormFields {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthdate: string;
  nationalId: string;
  nationality: string;
  sex: string;
  country: string;
  city: string;
  postCode: string;
  passportId: string;
  profilePicture: string;
}

const mapProfileToFormData = (profile: Partial<ProfileFormFields>): ProfileFormFields => {
  const birthdateValue = profile.birthdate as unknown;
  let normalizedBirthdate = '';
  if (birthdateValue) {
    if (typeof birthdateValue === 'string') {
      normalizedBirthdate = birthdateValue.split('T')[0];
    } else if (typeof birthdateValue === 'object') {
      const parsed = new Date(birthdateValue as Date);
      if (!Number.isNaN(parsed.valueOf())) {
        normalizedBirthdate = parsed.toISOString().split('T')[0];
      }
    }
  }

  return {
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    email: profile.email || '',
    phoneNumber: profile.phoneNumber || '',
    birthdate: normalizedBirthdate,
    nationalId: profile.nationalId || '',
    nationality: profile.nationality || '',
    sex: profile.sex || '',
    country: profile.country || '',
    city: profile.city || '',
    postCode: profile.postCode || '',
    passportId: profile.passportId || '',
    profilePicture: profile.profilePicture || '',
  };
};

const PersonalSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [gscDataLoaded, setGscDataLoaded] = useState(false);
  const [autoSubmitAttempted, setAutoSubmitAttempted] = useState(false);
  const [hasInitialGscData, setHasInitialGscData] = useState(false);
  const [pmsLockedFields, setPmsLockedFields] = useState({
    firstName: false,
    lastName: false,
    nationalId: false,
    passportId: false,
  });
  const formRef = React.useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthdate: '',
    nationalId: '',
    nationality: '',
    sex: '',
    country: '',
    city: '',
    postCode: '',
    passportId: '',
    profilePicture: '',
  });

  useEffect(() => {
    if (user) {
      setFormData(mapProfileToFormData(user as any));
    }
  }, [user]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setProfileLoading(false);
        return;
      }
      setProfileLoading(true);
      try {
        const response = await api.get('/users/profile');
        const userData = response.data;
        let gscData: any = null;

        // If user is a guest and has profileId and reservationId, fetch from GSC API
        if (user.role === 'guest' && (userData?.profileId || user?.profileId) && (userData?.reservationId || user?.reservationId)) {
          try {
            const profileId = userData?.profileId || user?.profileId;
            const reservationId = userData?.reservationId || user?.reservationId;
            const gscResponse = await api.get(`/users/guest-profile-from-gsc/${profileId}`, {
              params: { reservationId: reservationId }
            });
            gscData = gscResponse.data;
            console.log('[PersonalSettingsPage] GSC profile data fetched:', gscData);
          } catch (error) {
            console.error('Error fetching GSC profile data:', error);
            // Continue without GSC data
          }
        }

        // Merge userData and gscData, prioritizing GSC data for fields that are null/empty in userData
        const mergedData: any = { ...userData };

        const allowEditFromPms = userData?.hotelGuestPolicy?.allowEditGuestNameFromPms ?? false;

        if (gscData) {
          setPmsLockedFields({
            firstName: !!gscData.firstName && !allowEditFromPms,
            lastName: !!gscData.lastName && !allowEditFromPms,
            nationalId: !!gscData.nationalId && !allowEditFromPms,
            passportId: !!gscData.passportId && !allowEditFromPms,
          });
          // Use GSC data if userData field is empty/null, otherwise keep userData
          // Prioritize GSC data for all fields to ensure complete profile
          mergedData.firstName = gscData.firstName || userData?.firstName || '';
          mergedData.lastName = gscData.lastName || userData?.lastName || '';
          mergedData.email = gscData.email || userData?.email || '';
          mergedData.phoneNumber = gscData.phoneNumber || userData?.phoneNumber || '';
          
          // Handle birthdate - normalize format
          if (gscData.birthdate) {
            const gscBirthdate = typeof gscData.birthdate === 'string' 
              ? gscData.birthdate.split('T')[0] 
              : gscData.birthdate;
            mergedData.birthdate = gscBirthdate || (userData?.birthdate ? userData.birthdate.split('T')[0] : '');
          } else if (userData?.birthdate) {
            mergedData.birthdate = userData.birthdate.split('T')[0];
          } else {
            mergedData.birthdate = '';
          }
          
          mergedData.nationalId = gscData.nationalId || userData?.nationalId || '';
          mergedData.nationality = gscData.nationality || userData?.nationality || '';
          mergedData.country = gscData.country || userData?.country || '';
          mergedData.city = gscData.city || userData?.city || '';
          mergedData.postCode = gscData.postCode || userData?.postCode || '';
          mergedData.sex = gscData.sex || userData?.sex || '';
          mergedData.passportId = gscData.passportId || userData?.passportId || '';
        }

        // Check if we had empty fields before merging
        const hadEmptyFieldsBefore = !userData?.firstName || !userData?.lastName || !userData?.email || 
                                     (user.role === 'guest' && (!userData?.phoneNumber || !userData?.birthdate));
        
        setFormData(mapProfileToFormData(mergedData));
        
        // Mark that GSC data was loaded and check if it filled empty fields
        if (gscData && hadEmptyFieldsBefore) {
          setGscDataLoaded(true);
          setHasInitialGscData(true);
        } else if (gscData) {
          // GSC data loaded but fields were already filled
          setGscDataLoaded(false);
          setHasInitialGscData(false);
        } else {
          setPmsLockedFields({
            firstName: false,
            lastName: false,
            nationalId: false,
            passportId: false,
          });
          setGscDataLoaded(false);
          setHasInitialGscData(false);
        }
      } catch (err: any) {
        console.error('Failed to load profile:', err);
        setError(err.response?.data?.message || t('personalSettings.failedToLoadProfile'));
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [user, t]);

  // Auto-submit form after GSC data is loaded and form is valid
  // Only submit once when GSC data first loads and fills empty fields
  useEffect(() => {
    // Only auto-submit if:
    // 1. GSC data was loaded and filled empty fields
    // 2. We haven't attempted auto-submit yet
    // 3. Profile is not loading
    // 4. Form has valid required fields
    // 5. Not currently submitting
    if (hasInitialGscData && gscDataLoaded && !autoSubmitAttempted && !profileLoading && !loading) {
      // Check if form has valid required fields
      if (!formData.firstName || !formData.lastName || !formData.email) {
        return; // Don't submit if required fields are missing
      }
      
      // Wait a bit for form to be ready, then auto-submit (only once)
      const timer = setTimeout(async () => {
        if (formRef.current && !autoSubmitAttempted) {
          setAutoSubmitAttempted(true);
          
          // Validate form data before submitting
          const errors: Record<string, string> = {};
          
          if (!formData.firstName || !validateName(formData.firstName)) {
            errors.firstName = t('personalSettings.firstNameRequired');
          }
          
          if (!formData.lastName || !validateName(formData.lastName)) {
            errors.lastName = t('personalSettings.lastNameRequired');
          }
          
          if (!formData.email) {
            errors.email = t('personalSettings.emailRequired');
          } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
              errors.email = t('personalSettings.validEmail');
            }
          }
          
          if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
            errors.phoneNumber = t('personalSettings.invalidPhoneFormat');
          }

          // Identity document: validate FORMAT only when provided (never block
          // an auto-save just because an ID the PMS didn't supply is missing).
          if (isTurkishNationality(formData.nationality)) {
            if (formData.nationalId && formData.nationalId.trim() && !validateTCKN(formData.nationalId)) {
              errors.nationalId = t('validation.invalidTckn');
            }
          } else if (
            formData.passportId &&
            formData.passportId.trim() &&
            !validatePassport(formData.passportId, formData.nationality)
          ) {
            errors.passportId = t('validation.invalidPassport');
          }

          // If there are errors, don't submit
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setAutoSubmitAttempted(false);
            return;
          }
          
          // If form is valid, proceed with submission
          setError(null);
          setSuccess(null);
          setFieldErrors({});
          setLoading(true);
          
          try {
            // Prepare update data - only include fields that are allowed for the user's role
            const updateData: any = {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
            };

            // Add profile picture if it exists
            if (formData.profilePicture) {
              updateData.profilePicture = formData.profilePicture;
            }

            // Only include guest-specific fields if user is a guest
            if (user?.role === 'guest') {
              if (formData.phoneNumber) updateData.phoneNumber = formData.phoneNumber;
              if (formData.birthdate) updateData.birthdate = formData.birthdate;
              if (formData.nationalId) updateData.nationalId = formData.nationalId;
              if (formData.nationality) updateData.nationality = formData.nationality;
              if (formData.sex) updateData.sex = formData.sex;
              if (formData.country) updateData.country = formData.country;
              if (formData.city) updateData.city = formData.city;
              if (formData.postCode) updateData.postCode = formData.postCode;
              if (formData.passportId) updateData.passportId = formData.passportId;
            }

            await api.patch('/users/profile', updateData);

            // Refresh user data by reloading the page
            setTimeout(() => {
              window.location.reload();
            }, 1000);

            setSuccess(t('personalSettings.profileUpdated'));
          } catch (err: any) {
            console.error('Error updating profile:', err);
            setError(err.response?.data?.message || t('personalSettings.updateFailed'));
            setAutoSubmitAttempted(false); // Reset so it can try again if needed
          } finally {
            setLoading(false);
          }
        }
      }, 1000); // Small delay to ensure form is ready
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitialGscData, gscDataLoaded, autoSubmitAttempted, profileLoading, loading]);

  const validateName = (value: string): boolean => {
    const nameRegex = /^[a-zA-ZÀ-ÿĀ-žА-яА-Я\s'-]+$/;
    return nameRegex.test(value);
  };

  const validatePhone = (value: string): boolean => {
    const phoneRegex = /^\+?[0-9]*$/;
    if (value.includes(' ')) {
      return false;
    }
    if (value.startsWith('+') && value.length > 1) {
      return phoneRegex.test(value);
    }
    if (!value.startsWith('+')) {
      return phoneRegex.test(value);
    }
    return true;
  };

  const handleChange = (field: string, value: any) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (field === 'firstName' || field === 'lastName') {
      if (value && !validateName(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('personalSettings.onlyLetters'),
        }));
        return;
      }
    }

    if (field === 'phoneNumber') {
      if (value && !validatePhone(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('personalSettings.phoneFormatError'),
        }));
        return;
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/\/(jpg|jpeg|png|gif|webp)$/)) {
      setError(t('personalSettings.onlyImageFiles'));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('personalSettings.imageSizeLimit'));
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/upload/profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData(prev => ({ ...prev, profilePicture: response.data.url }));
      setSuccess(t('personalSettings.uploadSuccess'));
    } catch (err: any) {
      console.error('Error uploading profile picture:', err);
      setError(err.response?.data?.message || t('personalSettings.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    // Validate required fields
    const errors: Record<string, string> = {};

    if (!formData.firstName || !validateName(formData.firstName)) {
      errors.firstName = t('personalSettings.firstNameRequired');
    }

    if (!formData.lastName || !validateName(formData.lastName)) {
      errors.lastName = t('personalSettings.lastNameRequired');
    }

    if (!formData.email) {
      errors.email = t('personalSettings.emailRequired');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = t('personalSettings.validEmail');
      }
    }

    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      errors.phoneNumber = t('personalSettings.invalidPhoneFormat');
    }

    // Identity document: validate FORMAT only when provided. Editing your own
    // profile must not be blocked just because a national ID / passport that the
    // PMS never supplied is missing.
    if (isTurkishNationality(formData.nationality)) {
      if (formData.nationalId && formData.nationalId.trim() && !validateTCKN(formData.nationalId)) {
        errors.nationalId = t('validation.invalidTckn');
      }
    } else if (
      formData.passportId &&
      formData.passportId.trim() &&
      !validatePassport(formData.passportId, formData.nationality)
    ) {
      errors.passportId = t('validation.invalidPassport');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);

    try {
      // Prepare update data - only include fields that are allowed for the user's role
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      };

      // Add profile picture if it exists
      if (formData.profilePicture) {
        updateData.profilePicture = formData.profilePicture;
      }

      // Only include guest-specific fields if user is a guest
      if (user?.role === 'guest') {
        if (formData.phoneNumber) updateData.phoneNumber = formData.phoneNumber;
        if (formData.birthdate) updateData.birthdate = formData.birthdate;
        if (formData.nationalId) updateData.nationalId = formData.nationalId;
        if (formData.nationality) updateData.nationality = formData.nationality;
        if (formData.sex) updateData.sex = formData.sex;
        if (formData.country) updateData.country = formData.country;
        if (formData.city) updateData.city = formData.city;
        if (formData.postCode) updateData.postCode = formData.postCode;
        if (formData.passportId) updateData.passportId = formData.passportId;
      }

      await api.patch('/users/profile', updateData);

      // Refresh user data by fetching profile
      // Update the user in auth context by reloading the page
      // This ensures all components get the updated user data
      setTimeout(() => {
        window.location.reload();
      }, 1000);

      setSuccess(t('personalSettings.profileUpdated'));
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.message || t('personalSettings.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{t('personalSettings.pleaseLogin')}</Alert>
      </Container>
    );
  }

  if (profileLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            {t('personalSettings.loadingProfile')}
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, height: '100vh', overflow: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              {t('personalSettings.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('personalSettings.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <form ref={formRef} onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Profile Picture Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={formData.profilePicture}
                sx={{
                  width: 120,
                  height: 120,
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '3rem',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                }}
              >
                {!formData.profilePicture && (
                  <>{formData.firstName.charAt(0)}{formData.lastName.charAt(0)}</>
                )}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-picture-upload"
                type="file"
                onChange={handleImageUpload}
                disabled={uploading}
              />
              <label htmlFor="profile-picture-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploading ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
                  disabled={uploading}
                  sx={{ textTransform: 'none' }}
                >
                  {uploading ? t('personalSettings.uploading') : t('personalSettings.uploadProfilePicture')}
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t('personalSettings.imageFormats')}
              </Typography>
            </Box>

            <Divider />

            {/* Basic Information */}
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('personalSettings.basicInformation')}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('login.firstName')}
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  disabled={loading || pmsLockedFields.firstName}
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName || (pmsLockedFields.firstName ? t('personalSettings.lockedByPms', { defaultValue: 'PMS kaydindan geldigi icin degistirilemez.' }) : '')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#667eea', fontSize: '1.25rem' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('login.lastName')}
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  disabled={loading || pmsLockedFields.lastName}
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName || (pmsLockedFields.lastName ? t('personalSettings.lockedByPms', { defaultValue: 'PMS kaydindan geldigi icin degistirilemez.' }) : '')}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              required
              type="email"
              label={t('login.email')}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              disabled={loading}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#667eea', fontSize: '1.25rem' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Guest-specific fields - only show for guests */}
            {user.role === 'guest' && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('personalSettings.additionalInformation')}
                  </Typography>
                </Divider>

                <TextField
                  fullWidth
                  label={t('login.phoneNumber')}
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    let value = e.target.value;
                    value = value.replace(/\s/g, '');
                    if (value.includes('+') && value.indexOf('+') !== 0) {
                      value = value.replace(/\+/g, '');
                    }
                    handleChange('phoneNumber', value);
                  }}
                  disabled={loading}
                  error={!!fieldErrors.phoneNumber}
                  helperText={fieldErrors.phoneNumber || t('personalSettings.phoneFormat')}
                  inputProps={{
                    maxLength: 20,
                  }}
                />

                <TextField
                  fullWidth
                  type="date"
                  label={t('login.birthdate')}
                  value={formData.birthdate}
                  onChange={(e) => handleChange('birthdate', e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0],
                  }}
                />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('login.nationalId')}
                      value={formData.nationalId}
                      onChange={(e) => handleChange('nationalId', e.target.value)}
                      disabled={loading || pmsLockedFields.nationalId}
                      error={!!fieldErrors.nationalId}
                      inputProps={{ maxLength: 11 }}
                      helperText={fieldErrors.nationalId || (pmsLockedFields.nationalId ? t('personalSettings.lockedByPms', { defaultValue: 'PMS kaydindan geldigi icin degistirilemez.' }) : '')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('login.passportId')}
                      value={formData.passportId}
                      onChange={(e) => handleChange('passportId', e.target.value)}
                      disabled={loading || pmsLockedFields.passportId}
                      error={!!fieldErrors.passportId}
                      helperText={fieldErrors.passportId || (pmsLockedFields.passportId ? t('personalSettings.lockedByPms', { defaultValue: 'PMS kaydindan geldigi icin degistirilemez.' }) : '')}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('login.nationality')}
                      value={formData.nationality}
                      onChange={(e) => handleChange('nationality', e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('login.country')}
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('login.city')}
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('login.postCode')}
                      value={formData.postCode}
                      onChange={(e) => handleChange('postCode', e.target.value)}
                      disabled={loading}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? t('personalSettings.saving') : t('personalSettings.saveChanges')}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};

export default PersonalSettingsPage;

